// Homepage SEO rich-text placeholders — paste into Strapi HTML as plain text.

import type { ChartCandle } from "@/app/api/live/chart/route";

export const SEO_VARIABLE_IDS = [
  "zec_live_price",
  "zec_1_day_percentage_change",
  "zec_price_1_day_ago",
  "zec_price_24_hours_ago",
  "zec_30_day_percentage_change",
  "zec_return_ytd",
  "zec_all_time_high",
  "zec_percentage_change_to_ath",
] as const;

export type SeoVariableId = (typeof SEO_VARIABLE_IDS)[number];

export type SeoVariableValues = Partial<Record<SeoVariableId, string>>;

export interface SeoVariableDefinition {
  id: SeoVariableId;
  placeholder: string;
  description: string;
  source: string;
}

export const SEO_VARIABLE_DEFINITIONS: readonly SeoVariableDefinition[] = [
  {
    id: "zec_live_price",
    placeholder: "{{ zec_live_price }}",
    description: "Current ZEC price in the selected display currency.",
    source: "WebSocket live payload (same as the live price box).",
  },
  {
    id: "zec_1_day_percentage_change",
    placeholder: "{{ zec_1_day_percentage_change }}",
    description: "1-day price change as a signed percentage.",
    source: "WebSocket live payload (usd_price_24h_change_percentage).",
  },
  {
    id: "zec_price_1_day_ago",
    placeholder: "{{ zec_price_1_day_ago }}",
    description:
      "Implied ZEC price one day ago from current price and 1-day change % (same basis as the live price box).",
    source: "Derived from WebSocket live payload: usd_price / (1 + usd_price_24h_change_percentage/100).",
  },
  {
    id: "zec_price_24_hours_ago",
    placeholder: "{{ zec_price_24_hours_ago }}",
    description: "Deprecated alias for zec_price_1_day_ago (identical value).",
    source: "Same as zec_price_1_day_ago.",
  },
  {
    id: "zec_30_day_percentage_change",
    placeholder: "{{ zec_30_day_percentage_change }}",
    description: "Signed percentage change vs the candle at or before 30 days ago (USD), using 1M chart history.",
    source: "Binance klines (1M range) plus WebSocket live usd_price.",
  },
  {
    id: "zec_return_ytd",
    placeholder: "{{ zec_return_ytd }}",
    description:
      "Signed year-to-date return (USD): current price vs the first daily candle on or after Jan 1 from YTD chart history.",
    source: "Binance klines (YTD range) plus WebSocket live usd_price.",
  },
  {
    id: "zec_all_time_high",
    placeholder: "{{ zec_all_time_high }}",
    description:
      "Highest USD price in the full ALL-range history from the API plus the current live tick (not necessarily a global exchange ATH).",
    source: "Binance klines (ALL range) plus WebSocket live usd_price.",
  },
  {
    id: "zec_percentage_change_to_ath",
    placeholder: "{{ zec_percentage_change_to_ath }}",
    description:
      "Signed percentage move from the current USD price up to the ALL-range ATH (0% when price equals ATH).",
    source: "((ath_usd - current_usd) / current_usd) * 100; same ATH basis as zec_all_time_high.",
  },
];

/** @returns null if history is empty or data is unusable. */
export function compute30DayPercentageChangeUsd(
  candles: ChartCandle[],
  currentUsd: number
): number | null {
  const hist = candles
    .filter((c) => !c.is_gap)
    .sort(
      (a, b) =>
        new Date(a.candle_start).getTime() - new Date(b.candle_start).getTime()
    );
  if (hist.length === 0 || !(currentUsd > 0)) return null;
  const targetMs = Date.now() - 30 * 24 * 60 * 60 * 1000;
  let price30dAgo: number | null = null;
  for (let i = hist.length - 1; i >= 0; i--) {
    if (new Date(hist[i].candle_start).getTime() <= targetMs) {
      price30dAgo = hist[i].price;
      break;
    }
  }
  // If series starts slightly after the 30d mark, use earliest candle.
  if (price30dAgo === null) price30dAgo = hist[0].price;
  if (price30dAgo === 0) return null;
  return ((currentUsd - price30dAgo) / price30dAgo) * 100;
}

/** YTD return vs first candle on or after Jan 1 (local) in the YTD series. */
export function computeYtdReturnUsd(candles: ChartCandle[], currentUsd: number): number | null {
  const hist = candles
    .filter((c) => !c.is_gap)
    .sort(
      (a, b) =>
        new Date(a.candle_start).getTime() - new Date(b.candle_start).getTime()
    );
  if (hist.length === 0 || !(currentUsd > 0)) return null;
  const yearStartMs = new Date(new Date().getFullYear(), 0, 1).getTime();
  let priceYtdStart: number | null = null;
  for (let i = 0; i < hist.length; i++) {
    if (new Date(hist[i].candle_start).getTime() >= yearStartMs) {
      priceYtdStart = hist[i].price;
      break;
    }
  }
  if (priceYtdStart === null) priceYtdStart = hist[0].price;
  if (priceYtdStart === 0) return null;
  return ((currentUsd - priceYtdStart) / priceYtdStart) * 100;
}

export function formatSignedPercentage(pct: number): string {
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

/** Upside % from current price to ATH: (athUsd - currentUsd) / currentUsd * 100. */
export function computePercentageChangeToAth(currentUsd: number, athUsd: number): number | null {
  if (!(currentUsd > 0) || !(athUsd > 0)) return null;
  return ((athUsd - currentUsd) / currentUsd) * 100;
}

const PLACEHOLDER_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

const KNOWN_IDS = new Set<string>(SEO_VARIABLE_IDS);

function isSeoVariableId(s: string): s is SeoVariableId {
  return KNOWN_IDS.has(s);
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Default: prices and non-% fields (bold + accent gold). */
export const SEO_VARIABLE_VALUE_MARKUP_CLASS = "font-bold text-accent-gold";

/** Placeholders that show signed % — green if positive, red if negative (same as LivePriceBox). */
export const SEO_VARIABLE_SIGNED_PERCENT_IDS = [
  "zec_1_day_percentage_change",
  "zec_30_day_percentage_change",
  "zec_return_ytd",
  "zec_percentage_change_to_ath",
] as const satisfies readonly SeoVariableId[];

const SIGNED_PERCENT_ID_SET = new Set<string>(SEO_VARIABLE_SIGNED_PERCENT_IDS);

const EM_DASH_PLACEHOLDER = "\u2014";

function substitutionMarkupClass(id: SeoVariableId, value: string): string {
  const base = "font-bold";
  if (!SIGNED_PERCENT_ID_SET.has(id)) {
    return `${base} text-accent-gold`;
  }
  if (value === EM_DASH_PLACEHOLDER || value === "\u2013") {
    return `${base} text-foreground/70`;
  }
  const t = value.trimStart();
  if (t.startsWith("-")) return `${base} text-accent-red`;
  return `${base} text-accent-green`;
}

/** Replace `{{ variable_id }}` tokens; unknown ids are left unchanged. */
export function replaceSeoVariables(html: string, values: SeoVariableValues): string {
  return html.replace(PLACEHOLDER_RE, (full, rawId: string) => {
    if (!isSeoVariableId(rawId)) return full;
    const v = values[rawId];
    if (v === undefined) return full;
    const inner = escapeHtml(v);
    const cls = substitutionMarkupClass(rawId, v);
    return `<span class="${cls}">${inner}</span>`;
  });
}
